<script lang="ts">
  /* SEARCH — the entry point every competitor has and this had none of.
   *
   * ═══ LOCAL FIRST; OPTIONAL PLACES AFTERWARD ═══
   *
   * Everything worth searching is already in the browser, loaded to draw the
   * chart: the CHS current stations, both sets of tide stations, and the
   * ActiveCaptain notes. A host may also inject a geocoder, but chart-ui never
   * knows which service answers it. Local sailing data stays ahead of the more
   * general place results.
   *
   * ═══ AND A PASTED POSITION IS A RESULT ═══
   *
   * Cruising guides and the Sailing Directions give positions as degrees and
   * decimal minutes. Pasting one in is how a plan actually starts, and until
   * now there was nowhere to paste it. It is shown as its own section above the
   * name matches, because somebody who pasted a coordinate did not want a
   * fuzzy name hit.
   */
  import { parsePosition, searchItems, type SearchItem } from '@gennetay/chart-core/lib/search';
  import { formatPosition } from '@gennetay/chart-core/lib/search';
  import Empty from '../primitives/Empty.svelte';
  import Icon from '../primitives/Icon.svelte';
  import SectionHeader from '../primitives/SectionHeader.svelte';

  interface PlaceHit {
    name: string;
    lat: number;
    lon: number;
    detail?: string;
  }

  let {
    /** Everything searchable, flattened by the host. */
    items = [],
    /** Go there. The host owns what that means — moving the camera, opening the
     *  object sheet, dropping a waypoint. */
    onpick,
    /** Autofocus on open. Right on a desktop, where search is reached from the
     *  command bar or a slash key; wrong on a phone, where it would throw the
     *  keyboard up over the sheet the moment it appeared. */
    autofocus = false,
    geocode
  }: {
    items?: readonly SearchItem[];
    onpick?: (p: { lat: number; lon: number; label: string }) => void;
    autofocus?: boolean;
    geocode?: (q: string, signal: AbortSignal) => Promise<PlaceHit[]>;
  } = $props();

  let q = $state('');

  const position = $derived(parsePosition(q));
  const hits = $derived(searchItems(items, q));
  /* Grouped by kind, in the order the kinds first appear in the ranked list —
     so the best match's kind leads, rather than a fixed order putting tide
     stations above the anchorage somebody clearly meant. */
  const groups = $derived([...new Set(hits.map((h) => h.kind))]);

  let places = $state<PlaceHit[]>([]);
  let placeStatus = $state<'idle' | 'pending' | 'failed' | 'done'>('idle');

  $effect(() => {
    const query = q.trim();
    const lookup = geocode;
    const isPosition = position !== null;
    places = [];
    placeStatus = 'idle';
    if (!lookup || query.length < 3 || isPosition) return;

    const controller = new AbortController();
    let current = true;
    placeStatus = 'pending';
    const timer = setTimeout(async () => {
      try {
        const found = await lookup(query, controller.signal);
        if (!current) return;
        places = found;
        placeStatus = 'done';
      } catch {
        if (!current) return;
        places = [];
        placeStatus = 'failed';
      }
    }, 400);

    return () => {
      current = false;
      clearTimeout(timer);
      controller.abort();
    };
  });

  let input = $state<HTMLInputElement | null>(null);
  $effect(() => {
    if (autofocus) input?.focus();
  });
</script>

<label class="q">
  <Icon name="search" size={16} />
  <input
    bind:this={input}
    bind:value={q}
    type="search"
    placeholder="Stations, anchorages, coordinates…"
    aria-label="Search the chart"
  />
  {#if q}
    <button class="clear" onclick={() => (q = '')} aria-label="clear search">
      <Icon name="close" size={14} />
    </button>
  {/if}
</label>

{#if position}
  <SectionHeader title="Position" />
  <button
    class="res"
    onclick={() =>
      onpick?.({ lat: position.lat, lon: position.lon, label: formatPosition(position) })}
  >
    <span class="k">Go to</span>
    <span class="n">{formatPosition(position)}</span>
    <span class="v">{position.lat.toFixed(4)}, {position.lon.toFixed(4)}</span>
  </button>
{/if}

{#each groups as g (g)}
  <SectionHeader title={g} />
  {#each hits.filter((h) => h.kind === g) as h (h.kind + h.id)}
    <button class="res" onclick={() => onpick?.({ lat: h.lat, lon: h.lon, label: h.name })}>
      <span class="k">{h.kind}</span>
      <span class="n">{h.name}</span>
      <span class="v">{h.detail ?? ''}</span>
    </button>
  {/each}
{/each}

{#if placeStatus === 'pending' || placeStatus === 'failed' || places.length > 0}
  <SectionHeader title="Places" />
  {#if placeStatus === 'pending'}
    <p class="place-note">Searching places…</p>
  {:else if placeStatus === 'failed'}
    <p class="place-note">Place search isn't responding right now.</p>
  {:else}
    {#each places as place (`${place.lat},${place.lon},${place.name}`)}
      <button
        class="res"
        onclick={() => onpick?.({ lat: place.lat, lon: place.lon, label: place.name })}
      >
        <span class="k">Place</span>
        <span class="n">{place.name}</span>
        <span class="v">{place.detail ?? ''}</span>
      </button>
    {/each}
  {/if}
  <p class="attribution">Places © OpenStreetMap contributors</p>
{/if}

{#if q.trim() && !position && hits.length === 0 && placeStatus !== 'pending' && placeStatus !== 'failed' && places.length === 0}
  <Empty
    title="Nothing matches"
    body="Searching tide and stream stations, anchorages and notes, named places, and anything that parses as a position — 49°08.3'N 123°49.1'W, decimal degrees, or degrees-minutes-seconds."
  />
{:else if !q.trim()}
  <Empty
    title="Search the chart"
    body="Type a station or anchorage name, or paste a position out of a cruising guide."
  />
{/if}

<style>
  .q {
    display: flex;
    align-items: center;
    gap: var(--sp-4);
    min-height: var(--h-md);
    padding: 0 var(--sp-5);
    background: var(--s-2);
    border: 1px solid var(--line);
    border-radius: var(--r-2);
    color: var(--sub);
    font: var(--fs-body) / 1 var(--mono);
    margin-bottom: var(--sp-3);
  }
  .q:focus-within {
    border-color: var(--cy);
    outline: 2px solid var(--cy);
    outline-offset: -2px;
  }
  .q input {
    flex: 1;
    min-width: 0;
    border: 0;
    background: none;
    color: var(--ink);
    font: inherit;
    outline: none;
  }
  /* Safari draws its own X on `type=search`, in a place and a colour nobody
     chose. Ours is styled and keyboard-reachable. */
  .q input::-webkit-search-cancel-button {
    display: none;
  }
  .clear {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex: none;
    min-width: 24px;
    min-height: 24px;
    border: 0;
    background: none;
    color: var(--sub);
    cursor: pointer;
  }
  @media (pointer: coarse) {
    .q {
      min-height: var(--h-touch);
    }
    /* iOS Safari zooms the whole page when a focused control's text is under
       16 px, and a viewport that jumps as you start typing is worse than a
       slightly large field. */
    .q input {
      font-size: 16px;
    }
    .clear {
      min-width: var(--h-touch);
      min-height: var(--h-touch);
    }
  }

  .res {
    display: grid;
    grid-template-columns: 4.2em 1fr auto;
    gap: var(--sp-5);
    align-items: baseline;
    width: 100%;
    text-align: left;
    padding: var(--sp-3) var(--sp-4);
    background: none;
    border: 1px solid transparent;
    border-radius: var(--r-2);
    cursor: pointer;
    font: var(--fs-body) / var(--lh-ui) var(--mono);
    color: var(--ink);
    min-height: var(--h-touch);
  }
  .res:hover {
    background: var(--s-2);
  }
  .res:focus-visible {
    outline: 2px solid var(--cy);
    outline-offset: -1px;
  }
  /* Kind first, because whether a hit is a current station or somebody's
     anchorage opinion decides whether the row is worth reading at all. */
  .k {
    font: var(--fs-micro) / 1.4 var(--px);
    letter-spacing: var(--tr-label);
    color: var(--sub);
    text-transform: uppercase;
  }
  .v {
    color: var(--sub);
  }
  .place-note,
  .attribution {
    margin: 0;
    padding: var(--sp-3) var(--sp-4);
    color: var(--sub);
    font: var(--fs-small) / var(--lh-ui) var(--mono);
  }
  .attribution {
    padding-top: 0;
    font-size: var(--fs-micro);
  }
</style>
